import { hot } from 'react-hot-loader/root';
import React, { lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Loader } from 'shared';
import Layout from 'pages/Layout';
import ThemeProvider from 'pages/ThemeProvider';
import Container from 'components/Container';
import Authenticate from 'pages/Authenticate';
import AuthenticateRoute from 'pages/Login/AuthenticateRoute';
import Can, { actions, CanRoute } from 'components/Can';
import ChunkErrorBoundary from 'components/ChunkErrorBoundary';
import { page } from 'utils/access-rules/rules/routes';

// Code-Splitting (Forts. 79): die Route-Pages lazy laden -> CRA (react-scripts
// 3.4) gibt pro import() einen eigenen Chunk aus, statt alles in EINEN ~3,7-MB-
// Bundle zu packen. App-Shell (Layout/Container/ThemeProvider/Authenticate/Can)
// bleibt statisch, weil immer gerendert. <Suspense> faengt den Lade-Zustand,
// <ChunkErrorBoundary> faengt fehlgeschlagene Chunk-Loads (z.B. alter Client
// nach Deploy) -> einmaliger Auto-Reload statt Whitescreen.
const Todo = lazy(() => import('pages/Applicant/Todo'));
const Calendar = lazy(() => import('pages/CalendarPage'));
const Settings = lazy(() => import('pages/Settings'));
const ManageStudents = lazy(() => import('pages/ManageStudents'));
const Chat = lazy(() => import('pages/Chat'));
const InternshipReport = lazy(() => import('pages/InternshipReport'));
const Login = lazy(() => import('pages/Login'));
const ForgotPassword = lazy(() => import('pages/ForgotPassword'));
const NoMatch = lazy(() => import('pages/NoMatch'));
const UserManagerPage = lazy(() => import('pages/UserManagerPage'));
const ApplicantProfile = lazy(() => import('pages/ApplicantProfile/ApplicantProfileContainer'));
const YourApplications = lazy(() => import('pages/Applicant/YourApplications'));
const Assessment = lazy(() => import('pages/Assessment'));
const LearningTask = lazy(() => import('pages/Applicant/LearningTask'));
const InternshipComment = lazy(() => import('pages/InternshipComment'));
const Instructions = lazy(() => import('pages/Instructions'));
const RoadmapBoard = lazy(() => import('pages/Roadmap'));
const RoadmapItemDetail = lazy(() => import('pages/Roadmap/RoadmapItemDetail'));
const NotificationsOverviewPage = lazy(() => import('pages/NotificationsOverviewPage'));

function App() {
  return (
    <ThemeProvider>
      <Authenticate>
        <Layout>
          <Container>
            <ChunkErrorBoundary>
              <Suspense fallback={<Loader fullScreen />}>
                <Switch>
                  {/* exact: der hideChrome-Toggle in Layout.jsx matcht '/login' exakt —
                      die Route muss ebenso exakt matchen, sonst rendert '/login/'
                      Chrome UND full-bleed Stage gleichzeitig. Login hat keine Sub-Routes. */}
                  <Route exact path="/login" component={Login} />
                  <Route path="/forgot-password" component={ForgotPassword} />
                  <AuthenticateRoute>
                    <Switch>
                      <Route
                        exact
                        path="/"
                        render={() => (
                          <>
                            <Can I={actions.see} a={page.Todo}>
                              <Redirect to="/applicant/:internshipId?" />
                            </Can>
                            <Can I={actions.see} a={page.Applicants}>
                              <Redirect to="/applicants" />
                            </Can>
                          </>
                        )}
                      />
                      <CanRoute
                        to={page.ManageStudents}
                        path="/manage-students"
                        component={ManageStudents}
                      />
                      <CanRoute
                        to={page.Instructions}
                        path="/instructions"
                        component={Instructions}
                      />
                      {/* Applicant routes */}
                      <CanRoute
                        to={page.Todo}
                        path="/applicant/:internshipId?"
                        component={Todo}
                      />
                      <CanRoute
                        to={page.Settings}
                        path="/settings/:internshipId?"
                        component={Settings}
                      />
                      <CanRoute
                        to={page.Calendar}
                        path="/calendar/:internshipId?"
                        component={Calendar}
                      />
                      <CanRoute
                        to={page.Chat}
                        path="/chat/:internshipId?"
                        component={Chat}
                      />
                      <CanRoute
                        to={page.InternshipReport}
                        path="/internship-report/:internshipId?"
                        component={InternshipReport}
                      />
                      <CanRoute
                        to={page.InternshipComment}
                        path="/internship-comment/:internshipId?"
                        component={InternshipComment}
                      />
                      <CanRoute
                        to={page.YourApplications}
                        path="/your-applications/:internshipId?"
                        component={YourApplications}
                      />
                      <CanRoute
                        to={page.Assessment}
                        path="/your-assessment/:internshipId?"
                        component={Assessment}
                      />
                      <CanRoute
                        to={page.LearningTask}
                        path="/learning-task/:internshipId?"
                        component={LearningTask}
                      />
                      {/* Roadmap routes */}
                      <CanRoute
                        to={page.RoadmapItem}
                        path="/roadmap/item/:id"
                        component={RoadmapItemDetail}
                      />
                      <CanRoute
                        to={page.Roadmap}
                        path="/roadmap/:tab?"
                        component={RoadmapBoard}
                      />
                      {/* Teacher, Company, Education Provider routes */}
                      <CanRoute
                        to={page.Applicants}
                        path="/applicants"
                        component={UserManagerPage}
                      />
                      <CanRoute
                        to={page.ApplicantProfile}
                        path="/applicant-profile/:applicantId/:internshipId?"
                        component={ApplicantProfile}
                      />
                      {/* Alle Benachrichtigungen (Phase B) — recepient-gescopt, alle Rollen */}
                      <CanRoute
                        to={page.NotificationsOverview}
                        path="/notifications-overview"
                        component={NotificationsOverviewPage}
                      />
                      <Route component={NoMatch} />
                    </Switch>
                  </AuthenticateRoute>
                </Switch>
              </Suspense>
            </ChunkErrorBoundary>
          </Container>
        </Layout>
      </Authenticate>
    </ThemeProvider>
  );
}

export default hot(App);
